import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames/bind';

import Button from 'components/shared/button';
import Heading from 'components/shared/heading';

import styles from './face-recognition-hero-with-animation.module.scss';

const cx = classNames.bind(styles);
const P = '/animations/face-recognition';

const INITIAL_DELAY = 600;
const LASER_STEP = 350;
const LASER_HOLD = 600;
const DOOR_DELAY = 300;
const MAN_DELAY = 300;

// 3 - man all the time visible
// 12 - room, no doors
// 5, 6 - doors (visible/closed at start, hide = open after lasers)
// 7, 8, 9, 10, 11 - plant
// 0, 1, 2, 4 - lasers (scan sweep)

const LASER_FRAMES = [0, 1, 2, 4];

const FaceRecognitionHeroWithAnimation = ({ title, description, buttontext, buttonurl }) => {
  const [animationVisibilityRef, isAnimationStarted] = useInView({ triggerOnce: true });
  const [laserCount, setLaserCount] = useState(0);
  const [lasersGone, setLasersGone] = useState(false);
  const [doorsVisible, setDoorsVisible] = useState(true);
  const timeoutIdsRef = useRef([]);

  useEffect(() => {
    if (!isAnimationStarted) return undefined;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setDoorsVisible(false);
      return undefined;
    }

    const ids = timeoutIdsRef.current;
    const schedule = (fn, delay) => ids.push(setTimeout(fn, delay));
    const clearAll = () => {
      ids.forEach(clearTimeout);
      timeoutIdsRef.current = [];
    };

    let t = INITIAL_DELAY;

    for (let i = 1; i <= LASER_FRAMES.length; i += 1) {
      schedule(() => setLaserCount(i), t);
      t += LASER_STEP;
    }

    t += LASER_HOLD;
    schedule(() => setLasersGone(true), t);

    t += DOOR_DELAY;
    schedule(() => setDoorsVisible(false), t);

    t += MAN_DELAY;

    return clearAll;
  }, [isAnimationStarted]);

  const isLaserOn = (idx) => !lasersGone && idx < laserCount;

  return (
    <section className={cx('wrapper')} ref={animationVisibilityRef}>
      <div className="container">
        <Heading className={cx('title')} size="xl">
          {title}
        </Heading>
        <p className={cx('description')}>{description}</p>

        <Button className={cx('button')} to={buttonurl} theme="accent-primary">
          {buttontext}
        </Button>

        <div className={cx('animation')} aria-hidden>
          {/* z1: room + plant — always visible */}
          <img
            className={cx('frame', 'baseLayer')}
            src={`${P}/img_12.svg`}
            alt=""
            suppressHydrationWarning
          />
          {[7, 8, 9, 10, 11].map((f) => (
            <img
              key={f}
              className={cx('frame', 'baseLayer')}
              src={`${P}/img_${f}.svg`}
              alt=""
              suppressHydrationWarning
            />
          ))}

          {/* z2: doors — visible at start (closed), slide open to sides */}
          <img
            className={cx('frame', 'doorLayer', 'doorRight', { doorOpen: !doorsVisible })}
            src={`${P}/img_5.svg`}
            alt=""
            suppressHydrationWarning
          />
          <img
            className={cx('frame', 'doorLayer', 'doorLeft', { doorOpen: !doorsVisible })}
            src={`${P}/img_6.svg`}
            alt=""
            suppressHydrationWarning
          />

          {/* z3: man — hidden initially, fades in after doors open */}
          <img
            className={cx('frame', 'manLayer')}
            src={`${P}/img_3.svg`}
            alt=""
            suppressHydrationWarning
          />

          {/* z4: lasers — sweep one by one, then all fade out */}
          {LASER_FRAMES.map((f, i) => (
            <img
              key={f}
              className={cx('frame', 'laserLayer', { laserVisible: isLaserOn(i) })}
              src={`${P}/img_${f}.svg`}
              alt=""
              suppressHydrationWarning
            />
          ))}
        </div>
      </div>
    </section>
  );
};

FaceRecognitionHeroWithAnimation.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttontext: PropTypes.string.isRequired,
  buttonurl: PropTypes.string.isRequired,
};

export default FaceRecognitionHeroWithAnimation;
